import React, { useState, useEffect } from 'react';
import './GastroForm.css';

const GastroForm = ({ personaName, conversationId, onFormDataChange }) => {
  const [caseInfo, setCaseInfo] = useState(() => {
    const saved = localStorage.getItem('gastroCaseInfo');
    return saved ? JSON.parse(saved) : {
      dateOfSymptomOnset: '',
      occupation: '',
      hospitalAdmission: '',
      symptoms: {
        abdominalPain: '',
        diarrhoea: '',
        fever: '',
        headache: '',
        lethargy: '',
        nausea: '',
        vomiting: '',
        other: ''
      },
      dateOfSymptomResolution: '',
      foodExposures: {
        morningTea: {
          fruitPlatter: '',
          blueberryMuffins: '',
          savouryScones: '',
          herbalTeas: '',
          baristaCoffe: ''
        },
        lunchMains: {
          grilledChicken: '',
          pumpkinCannelloni: '',
          roastedVegetables: ''
        },
        lunchSides: {
          spinachSalad: '',
          quinoaSalad: '',
          breadRolls: ''
        },
        dessert: {
          fruitSkewers: '',
          lemonTarts: ''
        },
        afternoonTea: {
          glutenFreeBrownies: '',
          cheesePlatter: '',
          coffee: '',
          tea: '',
          sparklingWater: ''
        }
      },
      otherFoodsEaten: '',
      otherExposures: {
        antibiotics: '',
        contactWithSimilarIllness: '',
        contactWithFarmAnimals: '',
        contactWithPets: '',
        travelOverseas: ''
      }
    };
  });

  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    if (onFormDataChange) {
      const formData = {
        personaName,
        conversationId,
        dateOfSymptomOnset: caseInfo.dateOfSymptomOnset || '',
        occupation: caseInfo.occupation || '',
        hospitalAdmission: caseInfo.hospitalAdmission || '',
        symptoms: {
          abdominalPain: caseInfo.symptoms?.abdominalPain || '',
          diarrhoea: caseInfo.symptoms?.diarrhoea || '',
          fever: caseInfo.symptoms?.fever || '',
          headache: caseInfo.symptoms?.headache || '',
          lethargy: caseInfo.symptoms?.lethargy || '',
          nausea: caseInfo.symptoms?.nausea || '',
          vomiting: caseInfo.symptoms?.vomiting || '',
          other: caseInfo.symptoms?.other || ''
        },
        dateOfSymptomResolution: caseInfo.dateOfSymptomResolution || '',
        foodExposures: caseInfo.foodExposures || {},
        otherFoodsEaten: caseInfo.otherFoodsEaten || '',
        otherExposures: caseInfo.otherExposures || {}
      };
      onFormDataChange(formData);
    }
  }, [personaName, conversationId, caseInfo, onFormDataChange]);

  useEffect(() => {
    localStorage.setItem('gastroCaseInfo', JSON.stringify(caseInfo));
  }, [caseInfo]);

  const clearStoredData = () => {
    localStorage.removeItem('gastroCaseInfo');
  };

  const collectFinalData = () => {
    console.log(`[${new Date().toISOString()}] 📋 Collecting final gastro form data`);
    if (onFormDataChange) {
      const formData = {
        personaName,
        conversationId,
        dateOfSymptomOnset: caseInfo.dateOfSymptomOnset || '',
        occupation: caseInfo.occupation || '',
        hospitalAdmission: caseInfo.hospitalAdmission || '',
        symptoms: {
          abdominalPain: caseInfo.symptoms?.abdominalPain || '',
          diarrhoea: caseInfo.symptoms?.diarrhoea || '',
          fever: caseInfo.symptoms?.fever || '',
          headache: caseInfo.symptoms?.headache || '',
          lethargy: caseInfo.symptoms?.lethargy || '',
          nausea: caseInfo.symptoms?.nausea || '',
          vomiting: caseInfo.symptoms?.vomiting || '',
          other: caseInfo.symptoms?.other || ''
        },
        dateOfSymptomResolution: caseInfo.dateOfSymptomResolution || '',
        foodExposures: caseInfo.foodExposures || {},
        otherFoodsEaten: caseInfo.otherFoodsEaten || '',
        otherExposures: caseInfo.otherExposures || {}
      };
      onFormDataChange(formData);
      console.log(`[${new Date().toISOString()}] ✅ Final gastro data collected successfully`);
    }
  };

  useEffect(() => {
    if (!window.gastroFormRef) {
      window.gastroFormRef = {};
    }
    window.gastroFormRef.clearData = clearStoredData;
    window.gastroFormRef.collectFinalData = collectFinalData;

    return () => {
      if (window.gastroFormRef) {
        delete window.gastroFormRef.clearData;
        delete window.gastroFormRef.collectFinalData;
      }
    };
  }, [caseInfo, onFormDataChange, personaName, conversationId]);

  const handleChange = (e) => {
    setCaseInfo({ ...caseInfo, [e.target.name]: e.target.value });
  };

  const handleSymptomChange = (symptom, value) => {
    setCaseInfo({
      ...caseInfo,
      symptoms: {
        ...(caseInfo.symptoms || {}),
        [symptom]: value
      }
    });
  };

  const handleFoodExposureChange = (category, item, value) => {
    setCaseInfo({
      ...caseInfo,
      foodExposures: {
        ...(caseInfo.foodExposures || {}),
        [category]: {
          ...(caseInfo.foodExposures?.[category] || {}),
          [item]: value
        }
      }
    });
  };

  const handleOtherExposureChange = (field, value) => {
    setCaseInfo({
      ...caseInfo,
      otherExposures: {
        ...(caseInfo.otherExposures || {}),
        [field]: value
      }
    });
  };

  return (
    <div className="gastro-container">
      <div id="gastroForm" className="gastro-form">
        <div className="form-section">
          <h3 className="section-title">Case Information</h3>
          <div className="form-group">
            <label htmlFor="dateOfSymptomOnset">Date of symptom onset:</label>
            <input
              type="date"
              id="dateOfSymptomOnset"
              name="dateOfSymptomOnset"
              value={caseInfo.dateOfSymptomOnset}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="occupation">Occupation:</label>
            <input
              type="text"
              id="occupation"
              name="occupation"
              value={caseInfo.occupation}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter occupation"
            />
          </div>

          <div className="form-group">
            <label htmlFor="hospitalAdmission">Was the case admitted to hospital?</label>
            <select
              id="hospitalAdmission"
              name="hospitalAdmission"
              value={caseInfo.hospitalAdmission}
              onChange={handleChange}
              className="form-input"
            >
              <option value="">Select...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Symptoms</h3>
          <table className="symptoms-table">
            <thead>
              <tr>
                <th>Symptom</th>
                <th>Response</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Abdominal pain (e.g. cramping)</td>
                <td>
                  <select
                    value={caseInfo.symptoms?.abdominalPain || ''}
                    onChange={(e) => handleSymptomChange('abdominalPain', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Unsure">Unsure</option>
                  </select>
                </td>
              </tr>

              <tr>
                <td>Diarrhoea</td>
                <td>
                  <select
                    value={caseInfo.symptoms?.diarrhoea || ''}
                    onChange={(e) => handleSymptomChange('diarrhoea', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Unsure">Unsure</option>
                  </select>
                </td>
              </tr>

              <tr>
                <td>Fever</td>
                <td>
                  <select
                    value={caseInfo.symptoms?.fever || ''}
                    onChange={(e) => handleSymptomChange('fever', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Unsure">Unsure</option>
                  </select>
                </td>
              </tr>

              <tr>
                <td>Headache</td>
                <td>
                  <select
                    value={caseInfo.symptoms?.headache || ''}
                    onChange={(e) => handleSymptomChange('headache', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Unsure">Unsure</option>
                  </select>
                </td>
              </tr>

              <tr>
                <td>Lethargy</td>
                <td>
                  <select
                    value={caseInfo.symptoms?.lethargy || ''}
                    onChange={(e) => handleSymptomChange('lethargy', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Unsure">Unsure</option>
                  </select>
                </td>
              </tr>

              <tr>
                <td>Nausea</td>
                <td>
                  <select
                    value={caseInfo.symptoms?.nausea || ''}
                    onChange={(e) => handleSymptomChange('nausea', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Unsure">Unsure</option>
                  </select>
                </td>
              </tr>

              <tr>
                <td>Vomiting</td>
                <td>
                  <select
                    value={caseInfo.symptoms?.vomiting || ''}
                    onChange={(e) => handleSymptomChange('vomiting', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Unsure">Unsure</option>
                  </select>
                </td>
              </tr>

              <tr>
                <td>Other (specify)</td>
                <td>
                  <input
                    type="text"
                    value={caseInfo.symptoms?.other || ''}
                    onChange={(e) => handleSymptomChange('other', e.target.value)}
                    className="form-input"
                    placeholder="Describe other symptoms"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="form-section">
          <h3 className="section-title">Date of Symptom Resolution</h3>
          <div className="form-group">
            <label htmlFor="dateOfSymptomResolution">Date:</label>
            <input
              type="date"
              id="dateOfSymptomResolution"
              name="dateOfSymptomResolution"
              value={caseInfo.dateOfSymptomResolution}
              onChange={handleChange}
              className="form-input"
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Food Exposures</h3>
          <p className="info-text" style={{ marginBottom: '20px', marginTop: '0' }}>
            For each food item, indicate whether it was consumed: Yes or No
          </p>
          
          <div className="food-category">
            <h4 className="category-title">MORNING TEA</h4>
            <table className="food-table">
              <thead>
                <tr>
                  <th>Food Item</th>
                  <th>Consumed</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Fresh seasonal fruit platter (melon, berries, kiwi, grapes)</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.morningTea?.fruitPlatter || ''}
                      onChange={(e) => handleFoodExposureChange('morningTea', 'fruitPlatter', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td>Mini blueberry and bran muffins</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.morningTea?.blueberryMuffins || ''}
                      onChange={(e) => handleFoodExposureChange('morningTea', 'blueberryMuffins', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td>Savoury scones with cheddar and chive butter</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.morningTea?.savouryScones || ''}
                      onChange={(e) => handleFoodExposureChange('morningTea', 'savouryScones', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td>Assorted herbal teas</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.morningTea?.herbalTeas || ''}
                      onChange={(e) => handleFoodExposureChange('morningTea', 'herbalTeas', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td>Barista-style coffee</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.morningTea?.baristaCoffe || ''}
                      onChange={(e) => handleFoodExposureChange('morningTea', 'baristaCoffe', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="food-category">
            <h4 className="category-title">LUNCH - MAINS</h4>
            <table className="food-table">
              <thead>
                <tr>
                  <th>Food Item</th>
                  <th>Consumed</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Grilled chicken with lemon and thyme</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.lunchMains?.grilledChicken || ''}
                      onChange={(e) => handleFoodExposureChange('lunchMains', 'grilledChicken', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td>Pumpkin, ricotta and sage cannelloni</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.lunchMains?.pumpkinCannelloni || ''}
                      onChange={(e) => handleFoodExposureChange('lunchMains', 'pumpkinCannelloni', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td>Roasted vegetable medley (zucchini, pumpkin, carrot, capsicum)</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.lunchMains?.roastedVegetables || ''}
                      onChange={(e) => handleFoodExposureChange('lunchMains', 'roastedVegetables', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="food-category">
            <h4 className="category-title">LUNCH - SIDES</h4>
            <table className="food-table">
              <thead>
                <tr>
                  <th>Food Item</th>
                  <th>Consumed</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Spinach, rocket, and pear salad with parmesan and balsamic vinaigrette</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.lunchSides?.spinachSalad || ''}
                      onChange={(e) => handleFoodExposureChange('lunchSides', 'spinachSalad', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td>Quinoa and roast beetroot salad</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.lunchSides?.quinoaSalad || ''}
                      onChange={(e) => handleFoodExposureChange('lunchSides', 'quinoaSalad', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td>Fresh bread rolls with butter</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.lunchSides?.breadRolls || ''}
                      onChange={(e) => handleFoodExposureChange('lunchSides', 'breadRolls', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="food-category">
            <h4 className="category-title">DESSERT</h4>
            <table className="food-table">
              <thead>
                <tr>
                  <th>Food Item</th>
                  <th>Consumed</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Fruit skewers with dark chocolate drizzle</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.dessert?.fruitSkewers || ''}
                      onChange={(e) => handleFoodExposureChange('dessert', 'fruitSkewers', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td>Mini lemon tarts</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.dessert?.lemonTarts || ''}
                      onChange={(e) => handleFoodExposureChange('dessert', 'lemonTarts', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="food-category">
            <h4 className="category-title">AFTERNOON TEA</h4>
            <table className="food-table">
              <thead>
                <tr>
                  <th>Food Item</th>
                  <th>Consumed</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Gluten-free brownies</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.afternoonTea?.glutenFreeBrownies || ''}
                      onChange={(e) => handleFoodExposureChange('afternoonTea', 'glutenFreeBrownies', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td>Cheese platter with crackers and grapes</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.afternoonTea?.cheesePlatter || ''}
                      onChange={(e) => handleFoodExposureChange('afternoonTea', 'cheesePlatter', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td>Coffee</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.afternoonTea?.coffee || ''}
                      onChange={(e) => handleFoodExposureChange('afternoonTea', 'coffee', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td>Tea</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.afternoonTea?.tea || ''}
                      onChange={(e) => handleFoodExposureChange('afternoonTea', 'tea', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td>Sparkling water</td>
                  <td>
                    <select
                      value={caseInfo.foodExposures?.afternoonTea?.sparklingWater || ''}
                      onChange={(e) => handleFoodExposureChange('afternoonTea', 'sparklingWater', e.target.value)}
                      className="form-input"
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="form-group">
            <label htmlFor="otherFoodsEaten">Other foods eaten:</label>
            <textarea
              id="otherFoodsEaten"
              name="otherFoodsEaten"
              value={caseInfo.otherFoodsEaten}
              onChange={handleChange}
              className="form-textarea"
              placeholder="Describe any other foods consumed"
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Other Exposures</h3>
          
          <div className="form-group">
            <label htmlFor="antibiotics">Were antibiotics given to treat the illness?</label>
            <select
              id="antibiotics"
              name="antibiotics"
              value={caseInfo.otherExposures?.antibiotics || ''}
              onChange={(e) => handleOtherExposureChange('antibiotics', e.target.value)}
              className="form-input"
            >
              <option value="">Select...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>

          <div className="form-group">
            <label>In the two weeks before symptom onset, did any of the following apply?</label>
          </div>

          <div className="form-group">
            <label htmlFor="contactWithSimilarIllness">Contact with a family member, friend or colleague with similar illness</label>
            <select
              id="contactWithSimilarIllness"
              name="contactWithSimilarIllness"
              value={caseInfo.otherExposures?.contactWithSimilarIllness || ''}
              onChange={(e) => handleOtherExposureChange('contactWithSimilarIllness', e.target.value)}
              className="form-input"
            >
              <option value="">Select...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="contactWithFarmAnimals">Close contact with farm animals</label>
            <select
              id="contactWithFarmAnimals"
              name="contactWithFarmAnimals"
              value={caseInfo.otherExposures?.contactWithFarmAnimals || ''}
              onChange={(e) => handleOtherExposureChange('contactWithFarmAnimals', e.target.value)}
              className="form-input"
            >
              <option value="">Select...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="contactWithPets">Contact with pets (including fish and reptiles)</label>
            <select
              id="contactWithPets"
              name="contactWithPets"
              value={caseInfo.otherExposures?.contactWithPets || ''}
              onChange={(e) => handleOtherExposureChange('contactWithPets', e.target.value)}
              className="form-input"
            >
              <option value="">Select...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="travelOverseas">Travel overseas</label>
            <select
              id="travelOverseas"
              name="travelOverseas"
              value={caseInfo.otherExposures?.travelOverseas || ''}
              onChange={(e) => handleOtherExposureChange('travelOverseas', e.target.value)}
              className="form-input"
            >
              <option value="">Select...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
        </div>

        <p className="info-text">This form will be automatically submitted when you end the conversation.</p>
      </div>
      {feedback && <div className="feedback" dangerouslySetInnerHTML={{ __html: feedback }}></div>}
    </div>
  );
};

export default GastroForm;


